import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';

export default function TrialBalance() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    // Join straight through to journal_entries and page through results — an .in()
    // filter over a fetched-separately list of entry ids breaks once there are enough
    // entries to blow past the URL length limit (silently, since neither query below
    // used to check for errors), which is exactly what was happening at 1,600+ entries.
    const PAGE = 1000;
    let lines = [], from = 0;
    while (true) {
      let q = supabase
        .from('journal_lines')
        .select('base_debit, base_credit, account:account_id(id, account_code, account_name, account_types(category)), journal_entries!inner(status, entry_date, branch_id)')
        .eq('journal_entries.status', 'POSTED')
        .lte('journal_entries.entry_date', asOf);
      if (branchFilter) q = q.eq('journal_entries.branch_id', branchFilter);
      const { data, error } = await q.range(from, from + PAGE - 1);
      if (error) { console.error('TrialBalance load error:', error); setLoadError(error.message); break; }
      if (!data?.length) break;
      lines = lines.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }

    // Aggregate by account
    const map = {};
    (lines || []).forEach(l => {
      const acc = l.account;
      if (!acc) return;
      if (!map[acc.id]) map[acc.id] = { id: acc.id, account_code: acc.account_code, account_name: acc.account_name, category: acc.account_types?.category || 'OTHER', total_debit: 0, total_credit: 0 };
      map[acc.id].total_debit += parseFloat(l.base_debit || 0);
      map[acc.id].total_credit += parseFloat(l.base_credit || 0);
    });

    const result = Object.values(map).map(r => ({ ...r, balance: r.total_debit - r.total_credit }));
    result.sort((a, b) => a.account_code.localeCompare(b.account_code));
    setRows(result);
    setLoading(false);
  }, [asOf, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const categoryOrder = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
  const grouped = rows.reduce((g, r) => { const c = r.category || 'OTHER'; if (!g[c]) g[c] = []; g[c].push(r); return g; }, {});
  const totalDR = rows.reduce((s, r) => s + r.total_debit, 0);
  const totalCR = rows.reduce((s, r) => s + r.total_credit, 0);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Trial Balance</div><div className="page-subtitle">All debits and credits across all accounts</div></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div>
            <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>Branch</label>
            <select className="form-input" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>As of Date</label>
            <input className="form-input" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
          <button className="btn btn-secondary" style={{ marginTop: 20 }} onClick={() => window.print()}>Print</button>
        </div>
      </div>
      {loadError && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>Failed to load: {loadError}</div>}

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Debits</div><div className="stat-value amount" style={{ color: 'var(--danger)' }}>{totalDR.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Total Credits</div><div className="stat-value amount" style={{ color: 'var(--success)' }}>{totalCR.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Balanced</div><div className="stat-value" style={{ color: Math.abs(totalDR - totalCR) < 0.01 ? 'var(--success)' : 'var(--danger)' }}>{Math.abs(totalDR - totalCR) < 0.01 ? 'Yes' : 'Off by ' + Math.abs(totalDR - totalCR).toLocaleString()}</div></div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {categoryOrder.filter(c => grouped[c]).map(cat => {
            const catRows = grouped[cat];
            const catDR = catRows.reduce((s, r) => s + r.total_debit, 0);
            const catCR = catRows.reduce((s, r) => s + r.total_credit, 0);
            return (
              <div className="card" key={cat}>
                <div className="card-header">
                  <div className="card-title">{cat}</div>
                  <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                    <span style={{ color: 'var(--danger)' }}>DR: {catDR.toLocaleString()}</span>
                    <span style={{ color: 'var(--success)' }}>CR: {catCR.toLocaleString()}</span>
                  </div>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Code</th><th>Account Name</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
                    <tbody>
                      {catRows.map(r => (
                        <tr key={r.account_code} onClick={() => r.id && navigate(`/accounts/${r.id}/ledger?to=${asOf}`)} style={{ cursor: r.id ? 'pointer' : 'default' }}>
                          <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.account_code}</td>
                          <td>{r.account_name}</td>
                          <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{r.total_debit > 0 ? r.total_debit.toLocaleString() : ''}</td>
                          <td style={{ textAlign: 'right', color: 'var(--success)' }} className="amount">{r.total_credit > 0 ? r.total_credit.toLocaleString() : ''}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }} className="amount">{r.balance.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          <div className="card" style={{ borderColor: 'var(--primary)' }}>
            <div className="card-header">
              <div className="card-title">TOTALS</div>
              <div style={{ display: 'flex', gap: 32 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL DEBIT</div>
                  <div className="amount" style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)' }}>{totalDR.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL CREDIT</div>
                  <div className="amount" style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>{totalCR.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>BALANCE</div>
                  <div className="amount" style={{ fontSize: 18, fontWeight: 700, color: Math.abs(totalDR - totalCR) < 0.01 ? 'var(--success)' : 'var(--danger)' }}>
                    {Math.abs(totalDR - totalCR) < 0.01 ? 'Balanced' : (totalDR - totalCR).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
